import React from "react";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TankVisual from "../../TankVisual";
import TransferStatusBanner from "./TransferStatusBanner";
import TransferVolumeChart from "./TransferVolumeChart";
import TransferChecks from "./TransferChecks";
import TransferDayTable from "./TransferDayTable";
import { chipSx, nestedSurfaceSx, surfaceSx, useValueColor } from "./transferTheme";
import { formatGallons, toNumber } from "./transferMath";
import useNow from "./useNow";
import MDDatePicker from "components/MDDatePicker";
import moment from "moment/moment";

function Kpi({ label, value, helper }) {
  const valueColor = useValueColor();
  return (
    <Paper elevation={0} sx={(theme) => ({ ...nestedSurfaceSx(theme), p: 1.5, height: "100%" })}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">{label}</MDTypography>
      <MDTypography variant="h5" color={valueColor} fontWeight="bold">{value}</MDTypography>
      {helper && <MDTypography variant="caption" color="text">{helper}</MDTypography>}
    </Paper>
  );
}

function Section({ title, subtitle, children }) {
  const valueColor = useValueColor();
  return (
    <Paper elevation={0} sx={(theme) => ({ ...surfaceSx(theme), p: { xs: 2, md: 2.5 }, height: "100%" })}>
      <MDBox mb={2}>
        <MDTypography variant="h6" color={valueColor} fontWeight="bold">{title}</MDTypography>
        {subtitle && <MDTypography variant="caption" color="text">{subtitle}</MDTypography>}
      </MDBox>
      {children}
    </Paper>
  );
}

export default function TransferMonitorDialog({
  open,
  onClose,
  deviceName,
  assetId,
  deviceValue,
  deviceData,
  rs485Status,
  isLive,
  session,
  day,
  setBusinessDate,
  lastUpdatedLabel
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const valueColor = useValueColor();
  const now = useNow(1000, open);

  const tank = deviceData?.tank ?? {};
  const gallons = toNumber(deviceValue?.volume_gallons);
  const capacity = toNumber(deviceValue?.capacity_gallons);
  const transferActive = session?.status === "active";
  const lastSaved = day.lastSaved;
  const samples = session?.samples?.length
    ? session.samples
    : lastSaved ? session?.completedSamples?.[lastSaved.transfer.id] ?? [] : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      PaperProps={{ sx: ({ palette }) => ({ backgroundColor: palette.background.default, backgroundImage: "none" }) }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} px={3} pt={2.5} pb={1.5} flexWrap="wrap">
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <MDBox color="info" fontSize={24} lineHeight={1}><i className="fa-solid fa-truck-droplet" /></MDBox>
          <MDBox>
            <MDTypography variant="h5" color={valueColor} fontWeight="bold">Transfer Monitor · {deviceName}</MDTypography>
            {assetId && <MDTypography variant="caption" color="text">Asset {assetId}</MDTypography>}
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          {
            lastUpdatedLabel &&
            <MDTypography variant="caption" color="text" sx={{ mr: 1, opacity: 0.8 }}>
                Last updated {lastUpdatedLabel}
            </MDTypography>
          }
          <Chip size="small" sx={chipSx} label={isLive ? "LIVE" : "STALE"} color={isLive ? "success" : "error"} />
          <Chip size="small" sx={chipSx} label={`DESIRED: ${String(tank.desired_state ?? "--").toUpperCase()}`} />
          <Tooltip title="Close">
            <IconButton onClick={onClose} aria-label="Close transfer monitor">
              <i className="fa-solid fa-xmark" style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
        <TransferStatusBanner
          session={session}
          lastSaved={lastSaved}
          audit={deviceData?.audit}
          isLive={isLive}
          lastUpdated={deviceValue?.last_updated}
          now={now}
        />

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} lg={4}>
            <Section title="Tank" subtitle={session?.captureId ? `Capture ${session.captureId}` : "No open capture"}>
              <MDBox display="flex" justifyContent="center" sx={{ height: 240 }}>
                <MDBox sx={{ width: "100%", maxWidth: 320 }}>
                  <TankVisual
                    shape={tank.shape}
                    diameterVal={tank.diameter_inches}
                    percent_level={toNumber(deviceValue?.level_percent) ?? 0}
                    displayColor={session?.direction === "drop" ? theme.palette.primary.main : theme.palette.info.main}
                  />
                </MDBox>
              </MDBox>
              <Grid container spacing={1} mt={1}>
                <Grid item xs={6}><Kpi label="Start" value={formatGallons(session?.baseline)} helper={session?.baselineExact === false ? "since page opened" : null} /></Grid>
                <Grid item xs={6}><Kpi label="Now" value={formatGallons(gallons)} helper={`${Math.round(toNumber(deviceValue?.level_percent) ?? 0)}%`} /></Grid>
                <Grid item xs={6}><Kpi label="Room to full" value={capacity != null && gallons != null ? formatGallons(capacity - gallons) : "--"} /></Grid>
                <Grid item xs={6}><Kpi label="Pressure" value={`${Math.round(toNumber(deviceValue?.pressure_psi) ?? 0)} psi`} helper={deviceValue?.pressure_mode} /></Grid>
              </Grid>
            </Section>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Section title="Gallons during transfer" subtitle="Tank gallons per telemetry message, dashed line is the start">
              <TransferVolumeChart
                samples={samples}
                baseline={session?.baseline ?? lastSaved?.transfer?.startGallons}
                capacity={capacity}
                direction={session?.direction ?? lastSaved?.transfer?.type}
                height={300}
              />
            </Section>
          </Grid>

          <Grid item xs={12}>
            <Section title="Health & audit" subtitle="Evaluated on every telemetry message">
              <TransferChecks
                deviceValue={deviceValue}
                deviceData={deviceData}
                rs485Status={rs485Status}
                isLive={isLive}
                transferActive={transferActive}
              />
            </Section>
          </Grid>

          <Grid item xs={12}>
            <Section title="Today's transfers" subtitle={day.date ? `Table date ${day.date}` : null}>
              <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                <MDTypography variant="caption" color="text" fontWeight="bold">BUSINESS DATE:</MDTypography>
                <MDDatePicker
                  value={day.date ?? ""}
                  onChange={(date) => {
                    setBusinessDate(moment(date[0]).format("YYYY-MM-DD"))
                  }}
                  // options={{
                  //   dateFormat: "YYYY-MM-DD",
                  //   enableTime: false,
                  // }}
                  input={{ placeholder: "Date" }}
                />
              </MDBox>
              <TransferDayTable
                day={day}
                session={session}
                completedSamples={session?.completedSamples}
                capacity={capacity}
                onRefresh={day.refresh}
              />
            </Section>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
